import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useRef } from 'react';

import { Table, TableColumn, TableVariant } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { EmptyState, Loading } from '~/components';
import { RelativeTime } from '~/elements';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { CardAccount, CardAccountRepository } from '~/repositories/CardAccountRepository';
import { CardProgram, CardProgramRepository } from '~/repositories/CardProgramRepository';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { formatString } from '~/util';

const buildColumns = (cardProgramsMap: Map<string, CardProgram>): TableColumn<CardAccount>[] => [
  {
    header: 'Card Account ID',
    accessor: 'id',
    width: { min: 200, max: '3fr' },
    cell: ({ value }) => <Table.TextCell displayVariant="monospaced" weight="medium" value={value ?? ''} />,
  },
  {
    header: 'Card Program',
    accessor: 'cardProgramId',
    width: { min: 140, max: '2fr' },
    cell: ({ value }) => {
      const program = cardProgramsMap.get(value ?? '');
      return <Table.TextCell value={program?.description || value || '-'} />;
    },
  },
  {
    header: 'Account Type',
    id: 'accountType',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ row }) => {
      const program = cardProgramsMap.get(row.cardProgramId ?? '');
      return <Table.ChipCell value={program?.cardProgramType ? formatString(program.cardProgramType) : '-'} />;
    },
  },
  {
    header: 'Status',
    accessor: 'status',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.ChipCell value={value ? formatString(value as string) : '-'} />,
  },
  {
    header: 'Created',
    accessor: 'createdAt',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.Cell>{value ? <RelativeTime timestamp={value as Date} /> : '-'}</Table.Cell>,
  },
];

export const PageCardAccountsOverview: React.FC = () => {
  const navigate = useNavigate();
  const { currentPlatform, isSandbox } = useSessionStore();
  const {
    cursor,
    hasPrevious,
    onNext,
    onPrevious,
    pageIndex,
    pageSize,
    reset: resetPagination,
    setPageSize,
  } = usePaginationState();

  const isInitialRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    resetPagination();
  }, [currentPlatform?.id, isSandbox, resetPagination]);

  const {
    data: cardAccountsData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['card-accounts', 'list', currentPlatform?.id, isSandbox, pageSize, cursor],
    queryFn: () => CardAccountRepository.listCardAccounts({ limit: pageSize, startingAfter: cursor }),
    enabled: Boolean(currentPlatform?.id),
    placeholderData: keepPreviousData,
  });

  const { data: cardProgramsData } = useQuery({
    queryKey: ['card-programs', 'list-all', currentPlatform?.id, isSandbox],
    queryFn: () => CardProgramRepository.listCardPrograms(),
    enabled: Boolean(currentPlatform?.id),
  });

  const cardProgramsMap = useMemo(() => {
    const map = new Map<string, CardProgram>();
    (cardProgramsData?.cardPrograms ?? []).forEach((program) => {
      map.set(program.id, program);
    });
    return map;
  }, [cardProgramsData]);

  const cardAccounts = cardAccountsData?.cardAccounts ?? [];
  const hasMore = Boolean(cardAccountsData?.hasMore);

  return (
    <>
      <Loading isLoading={isLoading}>
        <Inner pt={0} px="var(--page-margin)">
          {isError ? (
            <EmptyState headline="Error" text="Failed to load card accounts." />
          ) : cardAccounts.length === 0 && !hasPrevious ? (
            <EmptyState headline="No card accounts" text="No card accounts found." />
          ) : (
            <Table<CardAccount>
              columns={buildColumns(cardProgramsMap)}
              data={cardAccounts}
              variant={TableVariant.List}
              pagination={{
                hasNext: hasMore,
                hasPrevious,
                pageIndex,
                pageSize,
                onNext: () => {
                  if (cardAccounts.length > 0) {
                    onNext({ cursor: cardAccounts[cardAccounts.length - 1].id });
                  }
                },
                onPrevious,
                onPageSizeChange: setPageSize,
              }}
              onRowClick={(row) => {
                navigate(`${ROUTE.CARD_ACCOUNTS}/edit/${row.id}`);
              }}
            />
          )}
        </Inner>
      </Loading>
    </>
  );
};
